/**
 * Cleans schedule rich-text HTML before it enters the editor.
 *
 *  - drops <br> tags that sit at the top level between block siblings
 *    (legacy imports often emit `<p>X</p><br><br><p>Y</p>` where each
 *    <p> already provides its own block spacing)
 *  - strips leading/trailing <br> inside <p>/<li>/headings/etc.
 *  - trims trailing whitespace and non-breaking spaces inside blocks
 *  - collapses runs of non-breaking + regular whitespace mid-text to a
 *    single regular space
 *  - removes block elements that, after cleaning, contain only
 *    whitespace or <br>s (legacy nbsp-only filler paragraphs)
 *
 * JS regex \s matches   (and other Unicode whitespace), so we do not
 * need to spell the non-breaking space out as a literal character.
 *
 * Mid-paragraph soft <br>s (single, intentional line breaks) are preserved.
 */

const BLOCK_SELECTOR = "p, li, h1, h2, h3, h4, h5, h6, blockquote, div, td, th"
const TRAILING_WHITESPACE = /\s+$/
const LEADING_WHITESPACE = /^\s+/
const WHITESPACE_RUN = /\s{2,}/g

export function cleanRichTextHtml(html) {
    if (typeof html !== "string" || !html.includes("<")) return html

    const container = document.createElement("div")
    container.innerHTML = html

    for (const node of [...container.childNodes]) {
        if (node.nodeType === 1 && node.tagName === "BR") {
            container.removeChild(node)
        }
    }

    for (const block of container.querySelectorAll(BLOCK_SELECTOR)) {
        while (block.firstChild?.nodeName === "BR") block.firstChild.remove()
        while (block.lastChild?.nodeName === "BR") block.lastChild.remove()
        collapseInternalWhitespace(block)
        trimTrailingWhitespace(block)
    }

    removeEmptyBlocks(container)

    return container.innerHTML

    function collapseInternalWhitespace(block) {
        const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT)
        const textNodes = []
        let node
        while ((node = walker.nextNode())) textNodes.push(node)
        for (const textNode of textNodes) {
            const collapsed = textNode.textContent.replace(WHITESPACE_RUN, " ")
            if (collapsed !== textNode.textContent) textNode.textContent = collapsed
        }
    }

    function trimTrailingWhitespace(block) {
        while (block.lastChild?.nodeType === 3) {
            const textNode = block.lastChild
            const trimmed = textNode.textContent.replace(TRAILING_WHITESPACE, "")
            if (trimmed === textNode.textContent) return
            if (trimmed === "") {
                textNode.remove()
                continue
            }
            textNode.textContent = trimmed
            return
        }
    }

    function removeEmptyBlocks(root) {
        // Reverse so children are processed before their parents: a parent that
        // becomes empty after its only child is removed is then removable in
        // the same pass. Without this, `<div><p><br></p></div>` would need two
        // passes (the cleaner would not be idempotent).
        for (const block of [...root.querySelectorAll(BLOCK_SELECTOR)].reverse()) {
            if (block.querySelector(BLOCK_SELECTOR)) continue
            if (block.tagName === "TD" || block.tagName === "TH") continue
            const text = block.textContent.replace(TRAILING_WHITESPACE, "").replace(LEADING_WHITESPACE, "")
            const hasMedia = block.querySelector("img, svg, video, audio, iframe, canvas")
            if (!text && !hasMedia) block.remove()
        }
    }
}
